/**
 * Forgot password — ELF-CIRCLE.
 *
 * Presentation only; the `forgotPassword` call and its result handling are the
 * template's. See ElfAuthShell for the two-panel chrome.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2, Check } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { useAuthContext } from '../context/AuthContext';
import { ElfAuthShell } from '../components';
import { route } from '../../../shared/utils';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const { forgotPassword } = useAuthContext();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setMessage('Please enter your email address');
      setIsSuccess(false);
      return;
    }

    setIsSubmitting(true);
    setMessage(null);

    try {
      const result = await forgotPassword({ email });
      if (result.success) {
        setIsSuccess(true);
        setMessage('Password reset instructions have been sent to your email');
      } else {
        setIsSuccess(false);
        setMessage(result.error || 'Failed to send password reset email. Please try again.');
      }
    } catch (error) {
      setIsSuccess(false);
      setMessage(
        error instanceof Error
          ? error.message
          : 'Failed to send password reset email. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Reset password"
        description="Reset your ELF-CIRCLE password."
        keywords="forgot password, reset password"
      />

      <ElfAuthShell
        kicker={isSuccess ? 'Sent' : 'Reset password'}
        title={isSuccess ? 'Check your email' : 'Forgot password'}
        lede={
          isSuccess
            ? undefined
            : 'Give us the address on the account and we’ll send a reset link.'
        }
        artHeadline="Locked out happens"
        artLede="A reset link puts you back in — no support ticket, no waiting on anyone."
      >
        {isSuccess ? (
          <div className="elf-auth__done">
            <span className="elf-auth__tick" aria-hidden="true">
              <Check />
            </span>
            <p className="elf-auth__note">
              {message} Nothing after a few minutes? Check the spam folder — the link is
              time-limited, so request another if it has expired.
            </p>
            <div className="elf-auth__stack">
              <button
                type="button"
                className="elf-auth__ghost"
                onClick={() => {
                  setIsSuccess(false);
                  setMessage(null);
                  setEmail('');
                }}
              >
                Send another email
              </button>
              <Link className="elf-auth__ghost" to={route('/auth/login')}>
                Back to sign in
                <ArrowRight aria-hidden="true" />
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="elf-auth__form" noValidate>
            {message && (
              <div className="elf-auth__err" role="alert">
                {message}
              </div>
            )}

            <label className="elf-auth__field">
              <span>Email</span>
              <input
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
              />
            </label>

            <button type="submit" className="pillcta elf-auth__submit" disabled={isSubmitting}>
              {isSubmitting ? 'Sending link…' : 'Send reset link'}
              <span aria-hidden="true">
                {isSubmitting ? <Loader2 className="elf-auth__spin" /> : <ArrowRight />}
              </span>
            </button>

            <p className="elf-auth__alt">
              Remembered it?{' '}
              <Link className="elf-auth__link" to={route('/auth/login')}>
                Back to sign in
              </Link>
            </p>
          </form>
        )}
      </ElfAuthShell>
    </>
  );
};

export default ForgotPasswordPage;
